<script setup>
import SearchableModel from "@/components/searchable-model/SearchableModel.vue";
import { computed, defineProps, onMounted, ref } from "vue";
import PaymentsList from "@/components/payment/PaymentsList.vue";
import CreatePayment from "@/components/payment/CreatePayment.vue";

const sortOptions = [
  { value: "created_at", label: "Date" },
  { value: "name", label: "Name" }
];

const {
  baseFilters,
  availableFilters,
  withSearch,
  includes,
  paymentModes,
  paymentStatus,
  payable,
} = defineProps({
  baseFilters: {
    type: Object,
    default: () => {}
  },
  availableFilters: {
    type: Array
  },
  withSearch: {
    type: Boolean,
    default: false
  },
  hasCreatePayment: {
    type: Boolean,
    default: false
  },
  paymentModes: {
    type: Array,
    default: () => [],
  },
  paymentStatus: {
    type: Array,
    default: () => [],
  },
  payable: {
    type: Object,
    default: () => {},
  },
  includes: {
    type: Array,
    default: () => [],
  },
});

const payments = ref([]);
const locale = document.documentElement.lang;

const showCreatePayment = ref(false);
const orderToPay = ref(payable);

const currency = orderToPay?.value?.total.currency ?? "EUR";
const payableId = computed(() => {
  return orderToPay?.value?.id ?? null;
});


const onClickPayOrder = () => {
  showCreatePayment.value = true;
};

const handlePaymentSaved = async (payment) => {
  showCreatePayment.value = false;
  document.location.reload()
};

const canAddPayment = () => {
  return payable?.registration
    && ["cart", "new"].includes(payable?.status)
    && payable.due_amount.amount > 0;
};

</script>

<template>
  <div>
    <SearchableModel
      model-name="Payment"
      load-on-start
      :withSearch="withSearch"
      :search-placeholder="$t('Search by payment reference or registration name')"
      sort="-created_at"
      :sort-options="sortOptions"
      :base-filters="baseFilters"
      :includes="includes"
      :available-filters="availableFilters"
      with-export
    >
      <template v-if="$slots['header-title']" #header-title>
        <slot name="header-title"></slot>
      </template>

      <template #header-actions>
        <slot name="header-actions"></slot>
      </template>

      <template #default="{ models, search }">

        <PaymentsList
          :payments="models"
          :search="search"
        >
          <template #line-prepend="{ payment }">
            <!--
              The order link and the customer are two separate answers. The payable is only
              sent when it was asked for, and a payment need not have been made by anyone on
              file, so neither may stand in for the cell: dropping it would shift every
              following value under the wrong heading.
            -->
            <td>
              <div v-if="payment.payable && payment.payable_type.includes('Order')">
                <strong>
                  <a :href="payment.payable.links.edit">
                    #{{ payment.payable_id }}
                  </a>
                </strong>
              </div>
              <a v-if="payment.registration" :href="payment.registration.links.show">
                <text-highlight :query="search" :splitBySpace="true">
                  {{ payment.registration.firstname }} {{ payment.registration.lastname }}
                </text-highlight>
              </a>
            </td>
          </template>
        </PaymentsList>
      </template>
    </SearchableModel>

    <button v-if="canAddPayment()" class="btn btn-sm btn-outline-primary" @click="onClickPayOrder()">
      <i class="fa fa-plus-circle"></i> {{ $t("Add payment") }}
    </button>

    <CreatePayment
      v-if="hasCreatePayment"
      :payable-type="'App\\Models\\Order'"
      :payable-id="payableId"
      :modes="paymentModes"
      :status-list="paymentStatus"
      :show="showCreatePayment && !! orderToPay"
      :due-amount="payable.due_amount.amount"
      :currency="currency"
      @payment:saved="handlePaymentSaved"
      @close="showCreatePayment = false"
    >
    </CreatePayment>
  </div>
</template>

<style scoped>

</style>